import { useState } from "react";
import { Button, Icon, Input, Modal, Popconfirm, Select, Space, Table, Tag, Text, useToast } from "@app/ui";
import { assignIdentifier, withdrawIdentifier } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import type { IdentifierGranularity, IdentifierScheme, ProductDetail } from "../types.js";

/** A product's identifiers: list, one-way withdraw, and the assign dialog. */
export function ProductIdentifiersPanel({
  orgId,
  product,
  canWrite,
  onChanged,
  onError,
}: {
  orgId: string;
  product: ProductDetail;
  canWrite: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const [assignOpen, setAssignOpen] = useState(false);
  const [scheme, setScheme] = useState<IdentifierScheme>("gs1");
  const [granularity, setGranularity] = useState<IdentifierGranularity>("model");
  const [lot, setLot] = useState("");
  const [serial, setSerial] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<unknown>(null);

  const withdraw = async (identifierId: string) => {
    try {
      await withdrawIdentifier(orgId, product.id, identifierId);
      toast.success(t("product.identifierWithdrawn"));
      onChanged();
    } catch (e) {
      onError(e);
    }
  };

  const submitAssign = async () => {
    setAssignError(null);
    setAssigning(true);
    try {
      await assignIdentifier(orgId, product.id, {
        scheme,
        granularity,
        ...(lot.trim() ? { lot: lot.trim() } : {}),
        ...(serial.trim() ? { serial: serial.trim() } : {}),
      });
      toast.success(t("product.identifierAssigned"));
      setAssignOpen(false);
      setLot("");
      setSerial("");
      onChanged();
    } catch (e) {
      setAssignError(e);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      {canWrite && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="sm"
            icon={<Icon name="plus" size={14} />}
            onClick={() => {
              setAssignError(null);
              setAssignOpen(true);
            }}
          >
            {t("product.assignIdentifier")}
          </Button>
        </div>
      )}
      <Table
        rowKey={(i) => i.id}
        empty={t("product.noIdentifiers")}
        columns={[
          { key: "scheme", title: t("product.scheme"), render: (i) => t(`idScheme.${i.scheme}`) },
          {
            key: "granularity",
            title: t("product.granularity"),
            render: (i) => t(`idGranularity.${i.granularity}`),
          },
          {
            key: "value",
            title: t("product.identifierValue"),
            render: (i) => (
              <Text code copyable>
                {i.value}
              </Text>
            ),
          },
          { key: "lot", title: t("product.lot"), render: (i) => i.lot ?? "—" },
          { key: "serial", title: t("product.serial"), render: (i) => i.serial ?? "—" },
          {
            key: "status",
            title: t("product.identifierStatus"),
            render: (i) =>
              i.status === "withdrawn" ? (
                <Tag color="default">{t("identifierStatus.withdrawn")}</Tag>
              ) : (
                <Tag color="success">{t("identifierStatus.active")}</Tag>
              ),
          },
          { key: "createdAt", title: t("common.createdAt"), render: (i) => fmtDate(i.createdAt) },
          ...(canWrite
            ? [
                {
                  key: "actions",
                  title: "",
                  render: (i: ProductDetail["identifiers"][number]) =>
                    i.status === "active" ? (
                      <Popconfirm
                        title={t("product.withdrawConfirm")}
                        description={t("product.withdrawDescription")}
                        okType="danger"
                        okText={t("product.withdrawIdentifier")}
                        cancelText={t("common.cancel")}
                        onConfirm={() => void withdraw(i.id)}
                      >
                        <Button size="sm" variant="danger">
                          {t("product.withdrawIdentifier")}
                        </Button>
                      </Popconfirm>
                    ) : null,
                },
              ]
            : []),
        ]}
        data={product.identifiers}
      />
      <Modal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={t("product.assignIdentifier")}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitAssign()}
        confirmLoading={assigning}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {assignError != null && <ErrorAlert error={assignError} onRetry={() => void submitAssign()} />}
          <Select
            label={t("product.scheme")}
            options={[
              { label: t("idScheme.gs1"), value: "gs1" },
              { label: t("idScheme.internal"), value: "internal" },
            ]}
            value={scheme}
            onChange={(v) => setScheme(v as IdentifierScheme)}
          />
          <Select
            label={t("product.granularity")}
            options={[
              { label: t("idGranularity.model"), value: "model" },
              { label: t("idGranularity.batch"), value: "batch" },
              { label: t("idGranularity.item"), value: "item" },
            ]}
            value={granularity}
            onChange={(v) => setGranularity(v as IdentifierGranularity)}
          />
          {scheme === "gs1" && granularity !== "model" && (
            <Input
              label={t("product.lot")}
              required={granularity === "batch"}
              help={t("product.lotHelp")}
              value={lot}
              onChange={(e) => setLot(e.target.value)}
            />
          )}
          {scheme === "gs1" && granularity === "item" && (
            <Input
              label={t("product.serial")}
              required
              help={t("product.serialHelp")}
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
            />
          )}
        </Space>
      </Modal>
    </>
  );
}
